import { Grid } from '../core/Grid';
import { Color, ColorMode, TRANSPARENT, rgb } from '../core/types';
import { quantize } from '../ansi/Palette';
import { BRAILLE_COLS, BRAILLE_ROWS, brailleFromMask, BRAILLE_BIT } from '../unicode/braille';
import { ASCII_RAMPS } from '../unicode/charsets';
import { quadrantChar } from '../unicode/blocks';
import { DitherMode, RgbBuffer, ditherBuffer, ditherMono, makeBuffer } from './dither';

/**
 * Image -> ANSI cells.
 *
 * The pipeline is deliberately UI-free so it can be tested and reused:
 *
 *   pixels -> resize -> aspect correction -> adjust -> [dither] -> sample -> cells
 *
 * Each mode differs only in the sampling step: how many image samples map to
 * one terminal cell, and which character represents them.
 */

export type ConversionMode =
  | 'block'
  | 'halfblock'
  | 'quarterblock'
  | 'braille'
  | 'ascii'
  | 'ascii-color';

export interface ConversionOptions {
  mode: ConversionMode;
  /** Target size in terminal cells. */
  width: number;
  height: number;
  /** Compensate for cells being taller than wide (usually 2.0). */
  aspectCorrection: number;
  brightness: number;
  contrast: number;
  saturation: number;
  dither: DitherMode;
  colorMode: ColorMode;
  /** Character ramp for the ASCII modes, darkest to lightest. */
  ramp: string;
  invert: boolean;
  /** Alpha below this becomes an empty cell. */
  alphaThreshold: number;
  /** Luminance threshold for Braille / mono modes, 0..255. */
  threshold: number;
  /** Background painted behind the art; TRANSPARENT keeps cells unpainted. */
  background: Color;
}

export const DEFAULT_CONVERSION: ConversionOptions = {
  mode: 'halfblock',
  width: 80,
  height: 24,
  aspectCorrection: 2,
  brightness: 0,
  contrast: 0,
  saturation: 0,
  dither: 'floyd-steinberg',
  colorMode: 'truecolor',
  ramp: ASCII_RAMPS.standard,
  invert: false,
  alphaThreshold: 24,
  threshold: 128,
  background: TRANSPARENT,
};

export interface SourceImage {
  width: number;
  height: number;
  /** RGBA, 8 bits per channel. */
  data: Uint8ClampedArray;
}

/** Samples per cell for a mode, horizontally and vertically. */
export function samplesPerCell(mode: ConversionMode): [number, number] {
  switch (mode) {
    case 'halfblock':
      return [1, 2];
    case 'quarterblock':
      return [2, 2];
    case 'braille':
      return [BRAILLE_COLS, BRAILLE_ROWS];
    default:
      return [1, 1];
  }
}

/**
 * Suggested cell size for an image, keeping its proportions.
 * Terminal cells are ~2x taller than wide, so the row count is halved.
 */
export function fitToCells(
  imageWidth: number,
  imageHeight: number,
  maxWidth: number,
  _mode: ConversionMode,
  aspectCorrection = 2,
): { width: number; height: number } {
  const width = Math.max(1, Math.round(maxWidth));
  const imageAspect = imageHeight / imageWidth;
  /*
   * A cell is `aspectCorrection` times taller than it is wide, so covering an
   * image of aspect r needs rows = cols * r / aspectCorrection.
   *
   * How many samples a mode takes inside a cell (1x1 for blocks, 2x4 for
   * Braille) deliberately plays no part: that is detail within the cell, not
   * the shape of the grid. Factoring it in stretched every conversion —
   * a square image came back twice as tall as it should be.
   */
  const height = Math.max(1, Math.round((width * imageAspect) / Math.max(0.1, aspectCorrection)));
  return { width, height };
}

/** Box-filter resize of RGBA data into an RGB float buffer, plus alpha. */
function resample(
  src: SourceImage,
  targetWidth: number,
  targetHeight: number,
): { rgbBuffer: RgbBuffer; alpha: Float32Array } {
  const out = makeBuffer(targetWidth, targetHeight);
  const alpha = new Float32Array(targetWidth * targetHeight);
  const xRatio = src.width / targetWidth;
  const yRatio = src.height / targetHeight;

  for (let y = 0; y < targetHeight; y++) {
    const sy0 = Math.floor(y * yRatio);
    const sy1 = Math.max(sy0 + 1, Math.floor((y + 1) * yRatio));
    for (let x = 0; x < targetWidth; x++) {
      const sx0 = Math.floor(x * xRatio);
      const sx1 = Math.max(sx0 + 1, Math.floor((x + 1) * xRatio));
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      let n = 0;
      for (let sy = sy0; sy < sy1 && sy < src.height; sy++) {
        for (let sx = sx0; sx < sx1 && sx < src.width; sx++) {
          const i = (sy * src.width + sx) * 4;
          const av = src.data[i + 3] / 255;
          r += src.data[i] * av;
          g += src.data[i + 1] * av;
          b += src.data[i + 2] * av;
          a += src.data[i + 3];
          n += 1;
        }
      }
      if (n === 0) n = 1;
      const o = (y * targetWidth + x) * 3;
      out.data[o] = r / n;
      out.data[o + 1] = g / n;
      out.data[o + 2] = b / n;
      alpha[y * targetWidth + x] = a / n;
    }
  }
  return { rgbBuffer: out, alpha };
}

function applyAdjustments(buffer: RgbBuffer, opts: ConversionOptions): void {
  const { data } = buffer;
  const contrast = (opts.contrast + 100) / 100;
  const brightness = opts.brightness * 2.55;
  const sat = (opts.saturation + 100) / 100;

  for (let i = 0; i < data.length; i += 3) {
    let r = data[i];
    let g = data[i + 1];
    let b = data[i + 2];

    r = (r - 128) * contrast + 128 + brightness;
    g = (g - 128) * contrast + 128 + brightness;
    b = (b - 128) * contrast + 128 + brightness;

    if (sat !== 1) {
      const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      r = lum + (r - lum) * sat;
      g = lum + (g - lum) * sat;
      b = lum + (b - lum) * sat;
    }
    if (opts.invert) {
      r = 255 - r;
      g = 255 - g;
      b = 255 - b;
    }
    data[i] = clamp8(r);
    data[i + 1] = clamp8(g);
    data[i + 2] = clamp8(b);
  }
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export function luminance(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export interface ConversionResult {
  grid: Grid;
  /** Sample resolution actually used. */
  sampleWidth: number;
  sampleHeight: number;
}

export function convertImage(src: SourceImage, options: Partial<ConversionOptions> = {}): ConversionResult {
  const opts: ConversionOptions = { ...DEFAULT_CONVERSION, ...options };
  const cols = Math.max(1, Math.round(opts.width));
  const rows = Math.max(1, Math.round(opts.height));
  const [sx, sy] = samplesPerCell(opts.mode);
  const sampleWidth = cols * sx;
  const sampleHeight = rows * sy;

  const { rgbBuffer, alpha } = resample(src, sampleWidth, sampleHeight);
  applyAdjustments(rgbBuffer, opts);

  if (opts.dither !== 'none' && opts.mode !== 'braille' && opts.mode !== 'ascii') {
    ditherBuffer(rgbBuffer, opts.dither, (r, g, b) => {
      const q = quantize(rgb(Math.round(r), Math.round(g), Math.round(b)), opts.colorMode);
      return [(q >> 16) & 0xff, (q >> 8) & 0xff, q & 0xff];
    });
  }

  const grid = new Grid(cols, rows);
  const at = (x: number, y: number): [number, number, number, number] => {
    const i = (y * sampleWidth + x) * 3;
    return [rgbBuffer.data[i], rgbBuffer.data[i + 1], rgbBuffer.data[i + 2], alpha[y * sampleWidth + x]];
  };
  const colorAt = (x: number, y: number): Color => {
    const [r, g, b] = at(x, y);
    return quantize(rgb(Math.round(r), Math.round(g), Math.round(b)), opts.colorMode);
  };

  switch (opts.mode) {
    case 'block': {
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const [, , , a] = at(x, y);
          if (a < opts.alphaThreshold) continue;
          grid.set(x, y, { char: '█', fg: colorAt(x, y), bg: opts.background, attrs: 0 });
        }
      }
      break;
    }

    case 'halfblock': {
      // Upper half block: foreground paints the top pixel, background the
      // bottom one, doubling vertical resolution at no cost.
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const topA = at(x, y * 2)[3];
          const bottomA = at(x, y * 2 + 1)[3];
          const topVisible = topA >= opts.alphaThreshold;
          const bottomVisible = bottomA >= opts.alphaThreshold;
          if (!topVisible && !bottomVisible) continue;
          if (topVisible && bottomVisible) {
            grid.set(x, y, {
              char: '▀',
              fg: colorAt(x, y * 2),
              bg: colorAt(x, y * 2 + 1),
              attrs: 0,
            });
          } else if (topVisible) {
            grid.set(x, y, { char: '▀', fg: colorAt(x, y * 2), bg: opts.background, attrs: 0 });
          } else {
            grid.set(x, y, { char: '▄', fg: colorAt(x, y * 2 + 1), bg: opts.background, attrs: 0 });
          }
        }
      }
      break;
    }

    case 'quarterblock': {
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const quads: Array<[number, number]> = [
            [x * 2, y * 2],
            [x * 2 + 1, y * 2],
            [x * 2, y * 2 + 1],
            [x * 2 + 1, y * 2 + 1],
          ];
          const visible = quads.map(([qx, qy]) => at(qx, qy)[3] >= opts.alphaThreshold);
          if (!visible.some(Boolean)) continue;
          const lums = quads.map(([qx, qy]) => {
            const [r, g, b] = at(qx, qy);
            return luminance(r, g, b);
          });
          const mean = lums.reduce((a, b) => a + b, 0) / 4;
          // Split the quadrants into "bright" and "dark" groups; the glyph
          // encodes which quadrants are bright, fg/bg carry the two colours.
          const mask = lums.map((l, i) => (visible[i] && l >= mean ? 1 : 0));
          const char = quadrantChar(!!mask[0], !!mask[1], !!mask[2], !!mask[3]);
          const brightIdx = mask.findIndex((m) => m === 1);
          const darkIdx = mask.findIndex((m) => m === 0);
          const fg = brightIdx >= 0 ? colorAt(quads[brightIdx][0], quads[brightIdx][1]) : opts.background;
          const bg = darkIdx >= 0 ? colorAt(quads[darkIdx][0], quads[darkIdx][1]) : opts.background;
          grid.set(x, y, { char, fg, bg, attrs: 0 });
        }
      }
      break;
    }

    case 'braille': {
      const lum = new Float32Array(sampleWidth * sampleHeight);
      for (let i = 0; i < lum.length; i++) {
        const o = i * 3;
        lum[i] = luminance(rgbBuffer.data[o], rgbBuffer.data[o + 1], rgbBuffer.data[o + 2]);
      }
      const on = ditherMono(lum, sampleWidth, sampleHeight, opts.threshold, opts.dither);
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          let mask = 0;
          let rSum = 0;
          let gSum = 0;
          let bSum = 0;
          let count = 0;
          for (let r = 0; r < BRAILLE_ROWS; r++) {
            for (let c = 0; c < BRAILLE_COLS; c++) {
              const px = x * BRAILLE_COLS + c;
              const py = y * BRAILLE_ROWS + r;
              const i = py * sampleWidth + px;
              if (at(px, py)[3] < opts.alphaThreshold) continue;
              if (on[i]) {
                mask |= BRAILLE_BIT[r][c];
                const o = i * 3;
                rSum += rgbBuffer.data[o];
                gSum += rgbBuffer.data[o + 1];
                bSum += rgbBuffer.data[o + 2];
                count += 1;
              }
            }
          }
          if (mask === 0) continue;
          const fg =
            count > 0
              ? quantize(
                  rgb(Math.round(rSum / count), Math.round(gSum / count), Math.round(bSum / count)),
                  opts.colorMode,
                )
              : TRANSPARENT;
          grid.set(x, y, { char: brailleFromMask(mask), fg, bg: opts.background, attrs: 0 });
        }
      }
      break;
    }

    case 'ascii':
    case 'ascii-color': {
      const ramp = opts.ramp.length > 0 ? opts.ramp : ASCII_RAMPS.standard;
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const [r, g, b, a] = at(x, y);
          if (a < opts.alphaThreshold) continue;
          const l = luminance(r, g, b) / 255;
          const index = Math.max(0, Math.min(ramp.length - 1, Math.round(l * (ramp.length - 1))));
          const char = ramp[index];
          if (char === ' ' && opts.background === TRANSPARENT) continue;
          grid.set(x, y, {
            char,
            fg: opts.mode === 'ascii-color' ? colorAt(x, y) : TRANSPARENT,
            bg: opts.background,
            attrs: 0,
          });
        }
      }
      break;
    }
  }

  return { grid, sampleWidth, sampleHeight };
}


/** Decodes an image file in the browser and returns raw pixels. */
export async function loadImageData(file: File | Blob): Promise<SourceImage> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Could not create a 2D context to decode the image.');
  ctx.drawImage(bitmap, 0, 0);
  const imageData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  bitmap.close();
  return { width: imageData.width, height: imageData.height, data: imageData.data };
}
